import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CandidateApiService } from '../../services/candidate-api.service';
import { QuizManagementService } from '@edtech-ui/core/services/quiz-management/quiz-management.service';
import { AiScreeningService } from '../../services/ai-screening.service';
import { RecruitmentJobService } from '@edtech-ui/core/services/recruitment-management/recruitment-job.service';
import { environment } from 'environments/environment';
import { CourseStatusEnum } from '../../../../constants/course-status.enum';
import { GetAllQuizResponse } from '@edtech-ui/core/models/quiz-review-interface.ts/get-all-quiz';
import { ToastrService } from 'ngx-toastr';
import { tap } from 'rxjs/internal/operators/tap';
import { finalize } from 'rxjs/internal/operators/finalize';
import { MatDialog } from '@angular/material/dialog';
import { AssignmentSubmitDialogBoxComponent } from '../assignment-submit-dialog-box/assignment-submit-dialog-box.component';
import { catchError, of } from 'rxjs';
import { MatTooltipModule } from '@angular/material/tooltip';

type CandidateStatus = 'Applied' | 'Interview Scheduled' | 'Shortlisted' | 'Selected' | 'Rejected' | 'New' | 'Assessment Pending' | 'Assessment Completed' | 'AI Interview Pending';

interface ActivityLog {
	status: CandidateStatus;
	comment: string;
	timestamp: Date;
}

@Component({
	selector: 'app-candidate-performance',
	standalone: true,
	imports: [CommonModule, FormsModule, MatTooltipModule],
	templateUrl: './candidate-performance.component.html',
	styleUrls: ['./candidate-performance.component.scss']
})
export class CandidatePerformanceComponent implements OnInit, OnDestroy {
	candidate: any = null;
	isLoading = false;
	private candidateId = '';

	// Status tracking
	selectedStatusToUpdate: CandidateStatus | '' = '';
	showConfirmPopup = false;
	isUpdatingStatus = false;

	// Comments
	newComment = '';
	activityLogs: ActivityLog[] = [];
	isSendingComment = false;

	// Toast
	showToast = false;
	private toastTimer: any;

	// Tab State
	activeReportTab: 'Assessment' | 'Interview' = 'Assessment';
	activeMainTab: 'job_applications' | 'profile_details' = 'job_applications';
	jobSearchQuery: string = '';
	selectedJobFilter: string = 'all';
	selectedStatusFilter: string = 'all';

	// Target Job Tracking for Assignments
	targetJobIdForAssessment: string = '';
	targetJobIdForAIInterview: string = '';
	activeStatusDropdownJobId: string | null = null;

	// Assign Job Modal State
	isAssignJobModalOpen: boolean = false;
	selectedJobToAssign: any = null;
	isAssigningJob: boolean = false;

	// Assessment assignment state
	isSelectAssessmentModalOpen = false;
	isSelectLinkModalOpen = false;
	availableAssessments: any[] = [];
	availableLinks: any[] = [];
	selectedQuizId = '';
	selectedAssessmentLink = '';
	isLoadingAssessments = false;
	isLoadingLinks = false;
	isAssigning = false;

	// Job assignment state
	isSelectJobModalOpen = false;
	availableJobs: any[] = [];
	selectedJobForInterviewId = '';
	selectedJobForInterview: any = null;
	isLoadingJobs = false;
	jobCurrentPage = 1;
	jobTotalPages = 1;
	jobLimit = 5;

	// AI Interview assignment state
	isSelectAIInterviewModalOpen = false;
	showSendInvitationPopup = false;
	availableRounds: any[] = [];
	selectedRoundId = '';
	selectedRound: any = null;
	isLoadingRounds = false;
	aiInterviewCurrentPage: number = 1;
	aiInterviewTotalPages: number = 1;
	aiInterviewHasNextPage: boolean = false;
	aiInterviewHasPreviousPage: boolean = false;
	aiInterviewLimit: number = 5;
	selectedRoundLink = '';

	resumeUrl: string = '';
	quizId = '';
	linkId = '';
	selectedAssessment: any = null;
	companyId = '';
	assignedAssessments: any[] = [];
	assessmentLink: string = '';
	assessmentName: string = '';
	assessmentLinkName: string = '';
	assignedDate: string = '';
	expiryDate: string = '';
	assessmentDuration: number = 0;
	assessmentId = '';
	jobId = '';
	isDeletingAssignment: string | null = null;
	isDeletingInterview: string | null = null;
	selectedAssessmentJobId: string = '';
	assignedAIInterviews: any[] = [];

	aiInterviewRole = '';
	aiInterviewDuration: number | null = null;
	aiInterviewAssignedAt = '';
	aiInterviewExpiry = '';
	aiInterviewLink = '';
	aiInterviewAssessmnetId = '';
	aiInterviewStatus = '';
	assignmentLinkName = '';
	assignmentLinkUrl = '';
	assessmentTimeLimit: number = 0;
	userName = '';
	userEmail = '';
	interviewId = '';
	showDeleteConfirmPopup = false;
	itemToDelete: { type: 'assessment' | 'interview', id: string } | null = null;
	showRightSidebar: boolean = false;
	showLogsModal: boolean = false;
	selectedItemForLogs: any = null;
	showUpdateStatusModal: boolean = false;
	selectedItemForUpdate: any = null;
	selectedItemType: 'job' | 'assessment' | 'interview' | null = null;
	newUpdateStatus: string = '';
	newUpdateComment: string = '';
	itemLogs: { [assignmentId: string]: any[] } = {};

	// Pagination
	assessmentPage = 1;
	interviewPage = 1;

	prevAssessmentPage(): void {
		if (this.assessmentPage > 1) {
			this.assessmentPage--;
		}
	}

	nextAssessmentPage(): void {
		if (this.assessmentPage < this.totalAssessmentPages) {
			this.assessmentPage++;
		}
	}

	get totalAssessmentPages(): number {
		return Math.ceil((this.assignedAssessments?.length || 0) / 5);
	}

	get paginatedAssessments(): any[] {
		const sorted = [...(this.assignedAssessments || [])].sort((a, b) => {
			const dateA = a.assigned_at ? new Date(a.assigned_at).getTime() : 0;
			const dateB = b.assigned_at ? new Date(b.assigned_at).getTime() : 0;
			return dateB - dateA;
		});
		if (this.assessmentPage > this.totalAssessmentPages) {
			this.assessmentPage = Math.max(1, this.totalAssessmentPages);
		}
		const start = (this.assessmentPage - 1) * 5;
		return sorted.slice(start, start + 5);
	}

	prevInterviewPage(): void {
		if (this.interviewPage > 1) {
			this.interviewPage--;
		}
	}

	nextInterviewPage(): void {
		if (this.interviewPage < this.totalInterviewPages) {
			this.interviewPage++;
		}
	}

	get totalInterviewPages(): number {
		return Math.ceil((this.assignedAiInterviews?.length || 0) / 5);
	}

	get paginatedAiInterviews(): any[] {
		const sorted = [...(this.assignedAiInterviews || [])].sort((a, b) => {
			const dateA = a.assigned_at ? new Date(a.assigned_at).getTime() : 0;
			const dateB = b.assigned_at ? new Date(b.assigned_at).getTime() : 0;
			return dateB - dateA;
		});
		if (this.interviewPage > this.totalInterviewPages) {
			this.interviewPage = Math.max(1, this.totalInterviewPages);
		}
		const start = (this.interviewPage - 1) * 5;
		return sorted.slice(start, start + 5);
	}


	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private candidateApi: CandidateApiService,
		private quizService: QuizManagementService,
		private aiScreeningService: AiScreeningService,
		private jobService: RecruitmentJobService,
		private toastr: ToastrService,
		private dialog: MatDialog,
	) { }

	ngOnInit(): void {
		this.companyId = localStorage.getItem('company_id') ?? '';
		const idParam = this.route.snapshot.paramMap.get('id');
		if (idParam) {
			this.candidateId = idParam;
			this.loadCandidate();
		}
	}



	loadCandidate(): void {
		this.isLoading = true;
		this.candidateApi.getCandidate(this.candidateId).pipe(
			tap((response) => {
				if (response.Success) {
					const data = response.Data;
					this.candidate = data;
					this.resumeUrl = data?.resume_url;
					this.userName = data.name;
					this.userEmail = data.email;
					this.assignedAssessments = data?.assigned_assessments ?? [];
					this.assignedAIInterviews = data?.assigned_ai_interviews ?? [];
					this.initializeMockItemLogs();
					const interview = this.assignedAIInterviews?.[0];

					if (interview) {
						this.aiInterviewRole = interview.job_role;
						this.aiInterviewDuration = interview.duration_minutes;
						this.aiInterviewAssignedAt = interview.assigned_at;
						this.aiInterviewExpiry = interview.expiry_date;
						this.aiInterviewLink = interview.interview_url;
						this.aiInterviewAssessmnetId = interview.assignment_id;
						this.aiInterviewStatus = interview.status;
					} else {
						this.aiInterviewLink = '';
					}

					if (this.candidate && !this.activityLogs.length) {
						this.activityLogs = (this.candidate.comments ?? []).map((c: any) => ({
							status: c.status ?? this.candidate.status,
							comment: c.comment ?? c.text ?? '',
							timestamp: c.created_at
								? new Date(c.created_at)
								: new Date(),
						}));

						if (!this.activityLogs.length) {
							this.activityLogs = [{
								status: this.mapStatus(this.candidate.status),
								comment: 'Application received',
								timestamp: this.candidate.created_at
									? new Date(this.candidate.created_at)
									: new Date(),
							}];
						}
					}
				} else {
					this.toastr.error(response.Message || 'Failed to load candidate');
				}
			}),
			catchError(() => {
				this.toastr.error('Failed to load candidate');
				return of(null);
			}),
			finalize(() => {
				this.isLoading = false;
			})
		).subscribe();
	}


	goBack(): void {
		this.router.navigate(['/recruiter-dashboard/candidates']);
	}

	onStatusChange(event: any): void {
		const newStatus = event.target.value as CandidateStatus;
		if (newStatus && newStatus !== this.mapStatus(this.candidate?.status)) {
			this.selectedStatusToUpdate = newStatus;
			this.showConfirmPopup = true;
			event.target.value = '';
		}
	}


	confirmStatusUpdate(): void {
		if (!this.candidate || !this.selectedStatusToUpdate) return;
		this.isUpdatingStatus = true;

		const backendStatus =
			this.reverseStatusMap[this.selectedStatusToUpdate] ??
			this.selectedStatusToUpdate.toLowerCase().replace(' ', '_');

		this.candidateApi.changeStatus(this.candidateId, backendStatus).pipe(
			tap((response) => {
				if (response.Success) {
					this.activityLogs.unshift({
						status: this.selectedStatusToUpdate as CandidateStatus,
						comment: `Status updated to ${this.selectedStatusToUpdate}`,
						timestamp: new Date(),
					});

					this.selectedStatusToUpdate = '';
					this.showConfirmPopup = false;
					this.loadCandidate();
					this.toastr.success(response.Message || 'Status updated successfully');
				} else {
					this.toastr.error(response.Message || 'Failed to update status');
				}
			}),
			catchError(() => {
				this.toastr.error('Failed to update status.');
				return of(null);
			}),
			finalize(() => {
				this.isUpdatingStatus = false;
			})
		).subscribe();
	}


	cancelStatusUpdate(): void {
		this.selectedStatusToUpdate = '';
		this.showConfirmPopup = false;
	}


	sendComment(): void {
		if (!this.newComment.trim() || !this.candidate) return;
		this.isSendingComment = true;
		const text = this.newComment.trim();

		this.candidateApi.addComment(this.candidateId, text).pipe(
			tap((response) => {
				if (response.Success) {
					this.activityLogs.unshift({
						status: this.mapStatus(this.candidate!.status),
						comment: text,
						timestamp: new Date(),
					});

					this.newComment = '';
					this.triggerToast();
				} else {
					this.toastr.error(response.Message || 'Failed to add comment');
				}
			}),
			catchError(() => {
				this.toastr.error('Failed to add comment.');
				return of(null);
			}),
			finalize(() => {
				this.isSendingComment = false;
			})
		)
			.subscribe();
	}

	private triggerToast(): void {
		this.showToast = true;
		if (this.toastTimer) clearTimeout(this.toastTimer);
		this.toastTimer = setTimeout(() => { this.showToast = false; }, 3000);
	}

	// ─── Assessment Assignment ──────────────────────────────────────────────────

	openSelectAssessmentModal(jobId?: string): void {
		console.log("openSelectAssessmentModal triggered with jobId:", jobId);
		this.isSelectLinkModalOpen = false;
		this.isSelectAssessmentModalOpen = true;
		this.availableAssessments = [];
		this.selectedQuizId = '';
		this.isLoadingAssessments = true;

		if (jobId) {
			this.targetJobIdForAssessment = jobId;
		}
		// NOTE: Do NOT pass company_id or job_id here.
		// Quizzes are created globally under the classroom, not per-company.
		// Filtering by company_id returns empty since quizzes aren't tagged with the recruiter's company_id.
		// The job_id is attached at assignment time via assignAssessment().
		const companyIdToUse = this.companyId || this.candidate?.company_id || localStorage.getItem('company_id') || '';

		this.quizService.getAllQuizes(
			environment.classroomId,
			1,
			50,
			'',
			'created_at',
			'desc',
			CourseStatusEnum.All,
			companyIdToUse,
			jobId
		).pipe(
			tap((response: GetAllQuizResponse) => {
				if (response.Success) {
					this.availableAssessments = response.Data.data;
				} else {
					this.toastr.error(response.Message || 'Failed to load assessments');
				}
			}),
			catchError(() => {
				this.toastr.error('Failed to load assessments');
				return of(null);
			}),
			finalize(() => {
				this.isLoadingAssessments = false;
			})
		).subscribe();
	}


	closeSelectAssessmentModal(): void {
		this.isSelectAssessmentModalOpen = false;
	}


	openSelectLinkModal(quizId: string, jobId?: string): void {
		if (jobId) {
			this.jobId = jobId;
		}
		this.selectedQuizId = quizId;
		this.isSelectAssessmentModalOpen = false;
		this.isSelectLinkModalOpen = true;
		this.availableLinks = [];
		this.selectedAssessmentLink = '';
		this.isLoadingLinks = true;

		this.quizService.getAllGenerateLinks(quizId, 1, 50).pipe(
			tap((response) => {
				if (response.Success) {
					const currentTargetJob = this.targetJobIdForAssessment || jobId || this.jobId;
					this.availableLinks = response.Data.data.map((link: any) => ({ ...link, job_id: currentTargetJob }));
				} else {
					this.toastr.error(
						response.Message || 'Failed to load assessment links'
					);
				}
			}),
			catchError(() => {
				this.toastr.error('Failed to load assessment links');
				return of(null);
			}),
			finalize(() => {
				this.isLoadingLinks = false;
			})
		).subscribe();
	}


	closeSelectLinkModal(): void {
		this.isSelectLinkModalOpen = false;
	}


	assignAssessment(): void {
		if (!this.selectedAssessment) return;

		this.isAssigning = true;
		const targetJob = this.targetJobIdForAssessment || this.selectedAssessment.job_id || this.jobId;
		const companyIdToUse = this.companyId || this.candidate?.company_id || localStorage.getItem('company_id') || '';

		const payload = {
			quiz_id: this.selectedQuizId || this.selectedAssessment.quiz_id,
			link_id: this.selectedAssessment.link_id || this.selectedAssessment.id || this.selectedAssessment._id,
			send_invitation: true
		};

		this.candidateApi.assignAssessment(
			this.candidateId,
			companyIdToUse,
			targetJob,
			payload
		).pipe(tap(response => {
			if (response.Success) {
				this.isSelectLinkModalOpen = false;
				this.selectedAssessment = null;
				this.targetJobIdForAssessment = '';
				this.loadCandidate();
				this.toastr.success(response.Message || 'Assessment assigned successfully');

				// Removed manual call to inviteCandidatesToLink here,
				// because the backend assignAssessment API already handles sending the email
				// when payload.send_invitation is true.
			} else {
				this.toastr.error(response.Message || 'Failed to assign assessment');
			}
		}),
			finalize(() => {
				this.isAssigning = false;
			}),
			catchError((err) => {
				this.toastr.error(err?.error?.detail ?? err?.error?.Message ?? 'Something went wrong. Please try again');
				return of(null);
			})
		).subscribe()
	}



	openSelectJobModal(): void {
		this.isSelectJobModalOpen = true;
		this.availableJobs = [];
		this.selectedJobForInterviewId = '';
		this.selectedJobForInterview = null;
		this.jobCurrentPage = 1;
		this.fetchJobsForInterview();
	}

	closeSelectJobModal(): void {
		this.isSelectJobModalOpen = false;
	}

	fetchJobsForInterview(): void {
		this.isLoadingJobs = true;
		this.jobService.getAllJobPosting(this.companyId, this.jobCurrentPage, this.jobLimit, 'active', '', {})
			.pipe(
				tap((response) => {
					if (response?.Success) {
						this.availableJobs = response.Data.jobs || [];
						this.jobTotalPages = response.Data.total_pages || 1;
					} else {
						this.toastr.error('Failed to load jobs');
					}
				}),
				catchError(() => {
					this.toastr.error('Failed to load jobs');
					return of(null);
				}),
				finalize(() => {
					this.isLoadingJobs = false;
				})
			).subscribe();
	}

	onJobPageChange(newPage: number): void {
		if (newPage >= 1 && newPage <= this.jobTotalPages) {
			this.jobCurrentPage = newPage;
			this.fetchJobsForInterview();
		}
	}

	selectJobForInterview(job: any): void {
		this.selectedJobForInterview = job;
		this.selectedJobForInterviewId = job.job_id;
	}

	confirmAssignJobToCandidate(): void {
		if (!this.selectedJobForInterviewId) return;
		this.isAssigningJob = true;
		this.candidateApi.assignJobToCandidate(this.candidateId, this.selectedJobForInterviewId).pipe(
			tap((response) => {
				if (response.Success) {
					this.isSelectJobModalOpen = false;
					this.selectedJobForInterviewId = '';
					this.selectedJobForInterview = null;
					this.loadCandidate();
					this.toastr.success(response.Message || 'Job assigned to candidate successfully');
				} else {
					this.toastr.error(response.Message || 'Failed to assign job');
				}
			}),
			catchError((err) => {
				this.toastr.error(err?.error?.detail ?? 'Failed to assign job');
				return of(null);
			}),
			finalize(() => {
				this.isAssigningJob = false;
			})
		).subscribe();
	}

	openSelectAIInterviewModalForJob(): void {
		if (!this.selectedJobForInterviewId) return;
		this.isSelectJobModalOpen = false;

		this.isSelectAIInterviewModalOpen = true;
		this.availableRounds = [];
		this.selectedRoundId = '';
		this.selectedRound = null;
		this.aiInterviewCurrentPage = 1;
		this.fetchAIInterviews();
	}

	openSelectAIInterviewModal(): void {
		this.isSelectAIInterviewModalOpen = true;
		this.availableRounds = [];
		this.selectedRoundId = '';
		this.selectedRound = null;
		this.aiInterviewCurrentPage = 1;
		this.fetchAIInterviews();
	}

	selectRound(round: any): void {
		this.selectedRound = round;
		this.selectedRoundId = round.id;
	}

	fetchAIInterviews(): void {
		this.isLoadingRounds = true;

		const params: any = { page: this.aiInterviewCurrentPage, limit: this.aiInterviewLimit };

		const companyIdToUse = this.companyId || this.candidate?.company_id || localStorage.getItem('company_id') || '';
		if (companyIdToUse) {
			params.company_id = companyIdToUse;
		}

		const targetJobId = this.targetJobIdForAIInterview || this.selectedJobForInterviewId || this.jobId;
		if (targetJobId) {
			params.job_id = targetJobId;
		}

		this.aiScreeningService.getRounds(params).pipe(
			tap((response) => {
				if (response.Success) {
					this.availableRounds = response.rounds ?? response.data ?? response.Data?.rounds ?? [];
					const pagination = response.pagination || response.Data?.pagination;
					if (pagination) {
						this.aiInterviewCurrentPage = pagination.current_page ?? this.aiInterviewCurrentPage;
						this.aiInterviewTotalPages = pagination.last_page ?? pagination.total_pages ?? this.aiInterviewTotalPages;
						this.aiInterviewHasNextPage = this.aiInterviewCurrentPage < this.aiInterviewTotalPages;
						this.aiInterviewHasPreviousPage = this.aiInterviewCurrentPage > 1;
					}
				} else {
					this.toastr.error(response.Message || 'Failed to load AI interview rounds');
				}
			}),
			catchError(() => {
				this.toastr.error('Failed to load AI interview rounds');
				return of(null);
			}),
			finalize(() => {
				this.isLoadingRounds = false;
			})
		).subscribe();
	}

	onAIInterviewPageChange(newPage: number): void {
		if (newPage >= 1 && newPage <= this.aiInterviewTotalPages) {
			this.aiInterviewCurrentPage = newPage;
			this.fetchAIInterviews();
		}
	}

	closeSelectAIInterviewModal(): void {
		this.isSelectAIInterviewModalOpen = false;
	}

	get selectedAIRoundDetails(): any {
		return this.selectedRound;
	}

	openSendInvitationPopup(): void {
		if (!this.selectedRoundId) return;
		this.isSelectAIInterviewModalOpen = false;
		this.showSendInvitationPopup = true;
	}

	closeSendInvitationPopup(): void {
		this.showSendInvitationPopup = false;
		this.isSelectAIInterviewModalOpen = true;
	}


	confirmAssignAIInterview(): void {
		const interviewUrl = this.selectedRound?.interview_url;

		if (!interviewUrl) {
			this.toastr.error('No interview selected.');
			return;
		}

		const urlParts = interviewUrl.split('/');
		const screeningRoundId = urlParts[urlParts.length - 1];
		this.isAssigning = true;
		const targetJobId = this.targetJobIdForAIInterview || this.selectedJobForInterviewId || this.jobId;
		const companyIdToUse = this.companyId || this.candidate?.company_id || localStorage.getItem('company_id') || '';
		this.candidateApi.assignAiInterview(this.candidateId, screeningRoundId, companyIdToUse, targetJobId).pipe(
			tap((response) => {
				if (response.Success) {
					this.showSendInvitationPopup = false;
					this.isSelectAIInterviewModalOpen = false;
					this.selectedRound = null;
					this.selectedRoundId = '';
					this.targetJobIdForAIInterview = '';
					this.loadCandidate();
					this.toastr.success(response.Message || 'Invitation sent successfully');
				} else {
					this.toastr.error(response.Message || 'Failed to send invitation');
				}
			}),
			catchError((err) => {
				this.toastr.error(err?.error?.detail ?? 'Something went wrong. Please try again');
				return of(null);
			}),
			finalize(() => {
				this.isAssigning = false;
			})
		)
			.subscribe();
	}



	isSystemOutcomeRejected(job: any): boolean {
		const outcome = (job?.system_outcome || '').toLowerCase();
		return outcome === 'rejected' || outcome === 'fail' || outcome === 'failed' || outcome === 'not selected';
	}

	canUpdateFinalStatus(job: any): boolean {
		const asm = job.assessment;
		const intv = job.interview;

		const asmAssigned = asm && (asm.is_assigned || asm.assignment_id || asm.url);
		const intvAssigned = intv && (intv.is_assigned || intv.assignment_id || intv.url);

		if (!asmAssigned && !intvAssigned) {
			return false;
		}

		let canUpdate = true;
		if (asmAssigned) {
			const asmStatus = (asm.status || asm.completion_status || asm.invitation_status || '').toLowerCase();
			const valid = ['completed', 'pass', 'passed', 'fail', 'failed', 'evaluated', 'submitted', 'auto submitted'];
			if (!valid.includes(asmStatus)) {
				canUpdate = false;
			}
		}
		if (intvAssigned) {
			const intvStatus = (intv.status || intv.completion_status || intv.invitation_status || '').toLowerCase();
			const valid = ['completed', 'pass', 'passed', 'fail', 'failed', 'evaluated', 'submitted', 'auto submitted'];
			if (!valid.includes(intvStatus)) {
				canUpdate = false;
			}
		}

		return canUpdate;
	}

	canAssignOrChangeAnything(job: any): boolean {
		if (!job) return true;

		// If final status is set to Selected or Rejected, block
		const finalStatus = (job.final_status || '').toLowerCase();
		if (['selected', 'rejected'].includes(finalStatus)) {
			return false;
		}

		// If assessment is in progress or done, block
		if (job.assessment) {
			const compStatus = (job.assessment.completion_status || '').toLowerCase();
			const invStatus = (job.assessment.invitation_status || '').toLowerCase();
			if (['in_progress', 'in progress', 'completed', 'submitted', 'auto submitted', 'pass', 'passed', 'fail', 'failed'].includes(compStatus) ||
				['in_progress', 'completed', 'submitted', 'pass', 'passed', 'fail', 'failed'].includes(invStatus)) {
				return false;
			}
		}

		// If interview is in progress or done, block
		if (job.interview) {
			const status = (job.interview.status || '').toLowerCase();
			const invStatus = (job.interview.invitation_status || '').toLowerCase();
			if (['in_progress', 'in progress', 'completed', 'submitted', 'graded', 'pass', 'passed', 'fail', 'failed'].includes(status) ||
				['in_progress', 'completed', 'submitted', 'graded', 'pass', 'passed', 'fail', 'failed'].includes(invStatus) ||
				(job.interview.ai_interview_score !== null && job.interview.ai_interview_score !== undefined)) {
				return false;
			}
		}

		return true;
	}

	canChangeAssessment(assessment: any): boolean {
		if (!assessment) return true;
		const compStatus = (assessment?.completion_status || '').toLowerCase();
		const invStatus = (assessment?.invitation_status || '').toLowerCase();
		if (['in_progress', 'in progress', 'completed', 'submitted', 'auto submitted', 'pass', 'passed', 'fail', 'failed'].includes(compStatus) ||
			['in_progress', 'completed', 'submitted', 'pass', 'passed', 'fail', 'failed'].includes(invStatus)) {
			return false;
		}
		return true;
	}

	@HostListener('document:click', ['$event'])
	onDocumentClick(event: MouseEvent): void {
		const target = event?.target as HTMLElement;
		if (!target || !target.closest('.status-dropdown-container')) {
			this.activeStatusDropdownJobId = null;
		}
	}

	toggleJobStatusDropdown(jobId: string, event: MouseEvent): void {
		event.stopPropagation();
		this.activeStatusDropdownJobId = this.activeStatusDropdownJobId === jobId ? null : jobId;
	}

	updateJobStatusQuick(job: any, newStatus: string): void {
		this.activeStatusDropdownJobId = null;
		const jobId = job.job_id;
		const lowerStatus = newStatus.toLowerCase();

		if (['selected', 'rejected', 'on_hold'].includes(lowerStatus)) {
			this.candidateApi.updateJobFinalStatus(this.candidateId, jobId, lowerStatus).pipe(
				tap((res) => {
					if (res.Success) {
						this.toastr.success(res.Message || `Status updated to ${this.formatStatusText(newStatus)}`);
						this.loadCandidate();
					} else {
						this.toastr.error(res.Message || 'Failed to update status');
					}
				}),
				catchError((err) => {
					this.toastr.error(err?.error?.detail ?? 'Failed to update status');
					return of(null);
				})
			).subscribe();
		} else {
			const backendStatus = this.reverseStatusMap[newStatus] || lowerStatus.replace(/\s+/g, '_');
			this.candidateApi.changeStatus(this.candidateId, backendStatus, jobId).pipe(
				tap((res) => {
					if (res.Success) {
						this.toastr.success(res.Message || `Status updated to ${this.formatStatusText(newStatus)}`);
						this.loadCandidate();
					} else {
						this.toastr.error(res.Message || 'Failed to update status');
					}
				}),
				catchError((err) => {
					this.toastr.error(err?.error?.detail ?? 'Failed to update status');
					return of(null);
				})
			).subscribe();
		}
	}

	get rawJobCards(): any[] {
		if (!this.candidate) return [];

		let jobs: any[] = [];
		if (this.candidate.job_applications && this.candidate.job_applications.length > 0) {
			jobs = this.candidate.job_applications;
		} else if (this.candidate.assigned_jobs && this.candidate.assigned_jobs.length > 0) {
			jobs = this.candidate.assigned_jobs;
		} else if (this.candidate.job_statuses && this.candidate.job_statuses.length > 0) {
			jobs = this.candidate.job_statuses;
		} else {
			jobs = [];
		}

		return jobs
			.map((job: any) => {
				const jId = job.job_id || job.id;
				let assessment = (this.assignedAssessments || []).find((a: any) => a.job_id === jId) ||
					((this.assignedAssessments || []).length === 1 && (!this.assignedAssessments[0].job_id || this.assignedAssessments[0].job_id === 'default_job') ? this.assignedAssessments[0] : null);
				if (!assessment) {
					assessment = job.assessment && (job.assessment.is_assigned || job.assessment.assignment_id || job.assessment.url) ? job.assessment : null;
				}

				let interview = (this.assignedAIInterviews || []).find((i: any) => i.job_id === jId) ||
					((this.assignedAIInterviews || []).length === 1 && (!this.assignedAIInterviews[0].job_id || this.assignedAIInterviews[0].job_id === 'default_job') ? this.assignedAIInterviews[0] : null);
				if (!interview) {
					interview = job.interview && (job.interview.is_assigned || job.interview.assignment_id || job.interview.url) ? job.interview : null;
				}

				let title = job.job_title || job.title || job.role;
				if (!title || title === 'Job Position' || title === 'Job Posting') {
					if (interview && (interview.job_role || interview.name)) {
						title = interview.job_role || interview.name;
					} else if (assessment && (assessment.job_title || assessment.quiz_name || assessment.name)) {
						title = assessment.job_title || assessment.quiz_name || assessment.name;
					} else if (this.candidate?.role) {
						title = this.candidate.role;
					} else {
						title = 'Job Position';
					}
				}

				let department = job.department;
				if (!department || department === 'General') {
					if (job.meta && job.meta.includes(' · ')) {
						department = job.meta.split(' · ')[0];
					} else if (this.candidate?.department || this.candidate?.subject) {
						department = this.candidate.department || this.candidate.subject;
					} else {
						department = 'General';
					}
				}

				return {
					job_id: jId,
					title: title,
					department: department,
					job_code: job.job_code || (jId && jId !== 'default_job' ? 'JD-' + jId.substring(0, 4).toUpperCase() : 'JD-1001'),
					applied_date: job.applied_at || job.created_at || job.assigned_at || this.candidate.created_at,
					system_outcome: this.formatStatusText(job.system_outcome || job.outcome || 'not_assigned'),
					final_status: this.formatStatusText(job.final_status || job.status || 'not_updated'),
					final_status_updated_by_name: job.final_status_updated_by_name,
					assessment: assessment,
					interview: interview,
					hasActionNeeded: !interview,
					default_passing_score: job.default_passing_score
				};
			});
	}

	get jobCardVMs(): any[] {
		const query = (this.jobSearchQuery || '').toLowerCase().trim();
		const filter = this.selectedJobFilter;
		const statusFilter = this.selectedStatusFilter;
		return this.rawJobCards.filter((card: any) => {
			if (filter === 'assigned') {
				if (!card.assessment && !card.interview) return false;
			} else if (filter === 'not_assigned') {
				if (card.assessment || card.interview) return false;
			} else if (filter !== 'all' && card.job_id !== filter) {
				return false;
			}
			
			if (statusFilter !== 'all' && card.final_status !== statusFilter) {
				return false;
			}
			if (!query) return true;
			return card.title.toLowerCase().includes(query) || card.job_code.toLowerCase().includes(query) || card.department.toLowerCase().includes(query);
		
		
		});

	}

	formatStatusText(statusText: string): string {
		if (!statusText) return 'Not Updated';
		return statusText
			.replace(/_/g, ' ')
			.replace(/\b\w/g, (char) => char.toUpperCase());
	}

	trackByJobId(index: number, job: any): string {
		return job.job_id || index.toString();
	}

	openSelectAssessmentModalForJob(jobId: string): void {
		console.log("openSelectAssessmentModalForJob triggered with jobId:", jobId);
		this.targetJobIdForAssessment = jobId;
		this.openSelectAssessmentModal(jobId);
	}

	openSelectAIInterviewModalForJobCard(jobId: string): void {
		console.log("openSelectAIInterviewModalForJobCard triggered with jobId:", jobId);
		this.targetJobIdForAIInterview = jobId;
		this.selectedJobForInterviewId = jobId;
		this.openSelectAIInterviewModal();
	}


	private readonly statusMap: Record<string, CandidateStatus> = {
		new: 'New',
		applied: 'Applied',
		assessment_pending: 'Assessment Pending',
		assessment_completed: 'Assessment Completed',
		ai_interview_pending: 'AI Interview Pending',
		interview_scheduled: 'Interview Scheduled',
		shortlisted: 'Shortlisted',
		selected: 'Selected',
		hired: 'Selected',
		rejected: 'Rejected'
	};

	private readonly reverseStatusMap: Record<string, string> = {
		Applied: 'applied', 'Interview Scheduled': 'interview_scheduled',
		Shortlisted: 'shortlisted',
		Selected: 'selected',
		Rejected: 'rejected',
		New: 'new',
		'Assessment Pending': 'assessment_pending',
		'Assessment Completed': 'assessment_completed',
		'AI Interview Pending': 'ai_interview_pending',
		'AI Interview Done': 'ai_interview_done',
	};

	mapStatus(status: string): CandidateStatus {
		return this.statusMap[status] ?? 'Applied';
	}

	get displayStatus(): CandidateStatus {
		return this.mapStatus(this.candidate?.status ?? '');
	}

	get candidateName(): string { return this.candidate?.name ?? ''; }
	get candidateEmail(): string { return this.candidate?.email ?? ''; }
	get candidateMobile(): string { return this.candidate?.mobile ?? ''; }
	get candidateLocation(): string { return this.candidate?.current_location ?? this.candidate?.city_pref_1 ?? '—'; }
	get candidateRole(): string { return this.candidate?.role ?? (this.candidate?.candidate_type === 'educator' ? 'Educator' : '—'); }
	get assessmentScore(): number { return this.candidate?.assessment_score ?? 0; }
	get aiInterviewScore(): number { return this.candidate?.ai_interview_score ?? 0; }

	get assignedAiInterviews(): any[] {
		if (this.candidate?.assigned_ai_interviews?.length) {
			return this.candidate.assigned_ai_interviews;
		}
		if (this.candidate?.ai_interview_link) {
			return [{
				interview_url: this.candidate.ai_interview_link,
				job_role: this.candidate.ai_interview_name,
				assigned_at: this.candidate.interview_assigned_date,
				expiry_date: this.candidate.interview_expiry_date,
				duration_minutes: this.candidate.interview_duration,
				status: this.candidate.ai_interview_status || 'invited',
				session_id: this.candidate.ai_interview_session_id,
				unique_token: this.candidate.ai_interview_unique_token
			}];
		}
		return [];
	}


	getStatusBadgeClass(status: string): string {
		switch (status) {
			case 'Shortlisted': return 'badge-shortlisted';
			case 'Selected': return 'badge-selected';
			case 'Applied': return 'badge-applied';
			case 'Interview Scheduled': return 'badge-interview';
			case 'Rejected': return 'badge-rejected';
			default: return 'badge-default';
		}
	}

	getInitials(name: string): string {
		if (!name) return '??';
		return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
	}

	getScoreColorClass(score: number): string {
		if (score >= 85) return 'score-excellent';
		if (score >= 65) return 'score-good';
		return 'score-poor';
	}

	getDuration(timeLimit: number | null | undefined): string {
		if (!timeLimit) {
			return '—';
		}

		const minutes = timeLimit / 60;

		if (minutes >= 60) {
			const hours = Math.floor(minutes / 60);
			const remainingMinutes = minutes % 60;

			return remainingMinutes
				? `${hours} hr ${remainingMinutes} min`
				: `${hours} hr`;
		}

		return `${minutes} min`;
	}

	formatDuration(minutes: number | null | undefined): string {
		if (!minutes) return '0 min';
		const totalMinutes = Math.round(minutes);
		if (totalMinutes <= 60) {
			return `${totalMinutes} min`;
		}
		const hrs = Math.floor(totalMinutes / 60);
		const mins = totalMinutes % 60;
		return mins > 0 ? `${hrs} hr : ${mins} min` : `${hrs} hr`;
	}


	downloadResume(url: string): void {
		if (!url) {
			return;
		}

		const link = document.createElement('a');
		link.href = url;
		link.target = '_blank';
		link.download = '';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	}


	copyToClipboard(text: string): void {
		if (!text) {
			this.toastr.warning('No link available to copy');
			return;
		}

		navigator.clipboard.writeText(text).then(() => {
			this.toastr.success('Link copied successfully');
		});
	}


	showReportWarningPopup = false;
	reportWarningTitle = '';
	reportWarningMessage = '';
	reportWarningType: 'info' | 'error' = 'info';

	openReportWarning(title: string, message: string, type: 'info' | 'error' = 'info') {
		this.reportWarningTitle = title;
		this.reportWarningMessage = message;
		this.reportWarningType = type;
		this.showReportWarningPopup = true;
	}

	closeReportWarning() {
		this.showReportWarningPopup = false;
	}

	goToAssessmentReport(assessment: any) {
		const compStatus = assessment?.completion_status?.toLowerCase();
		const invStatus = assessment?.invitation_status?.toLowerCase();

		if (['submitted', 'auto submitted', 'completed', 'pass', 'passed', 'fail', 'failed'].includes(compStatus) || ['pass', 'passed', 'fail', 'failed'].includes(invStatus)) {
			const attemptId = assessment?.attempt_id || this.assessmentId;
			if (!attemptId) {
				this.openReportWarning('Report Not Found', 'No assessment attempt found for this candidate.', 'error');
				return;
			}
			this.router.navigate(['/assesment-report', attemptId]);
		} else if (compStatus === 'in progress') {
			this.openReportWarning('Assessment In Progress', 'The candidate is currently taking this assessment. The report will be available once they finish.');
		} else if (compStatus === 'not_started' || !compStatus) {
			if (invStatus === 'pending') {
				this.openReportWarning('Invitation Pending', 'The assessment invitation is still pending to be sent to the candidate.');
			} else if (invStatus === 'failed') {
				this.openReportWarning('Invitation Failed', 'The assessment invitation email failed to send. Please try assigning it again.', 'error');
			} else {
				this.openReportWarning('Not Started', 'This candidate has not yet completed the assigned assessment. The report will be available once it is completed.');
			}
		} else {
			this.openReportWarning('Assessment Status', `Current status: ${compStatus || 'Unknown'}. Report is not available yet.`);
		}
	}

	goToAIInterviewReport(interview: any) {
		const status = interview?.status?.toLowerCase() || '';
		const invStatus = interview?.invitation_status?.toLowerCase() || '';

		if (['completed', 'graded', 'pass', 'passed', 'fail', 'failed'].includes(status) || ['pass', 'passed', 'fail', 'failed', 'graded'].includes(invStatus)) {
			if (interview.session_id && interview.unique_token) {
				this.router.navigate(['/ai-performance-report', this.candidateId, interview.session_id, interview.unique_token]);
			} else {
				this.openReportWarning('Report Not Found', 'Interview session details are missing.', 'error');
			}
		} else if (status === 'in_progress' || invStatus === 'in_progress') {
			this.openReportWarning('Interview In Progress', 'The candidate is currently taking the AI interview. The report will be available once they finish.');
		} else {
			if (invStatus === 'pending') {
				this.openReportWarning('Invitation Pending', 'The interview invitation is still pending to be sent to the candidate.');
			} else if (invStatus === 'failed') {
				this.openReportWarning('Invitation Failed', 'The interview invitation email failed to send. Please try assigning it again.', 'error');
			} else {
				this.openReportWarning('Not Started', 'This candidate has not yet completed the assigned interview. The report will be available once it is completed.');
			}
		}
	}

	deleteCandidateAssignment(assignmentId: string) {
		this.itemToDelete = { type: 'assessment', id: assignmentId };
		this.showDeleteConfirmPopup = true;
	}

	deleteCandidateInterview(interviewId: string) {
		this.itemToDelete = { type: 'interview', id: interviewId };
		this.showDeleteConfirmPopup = true;
	}

	cancelDelete() {
		this.showDeleteConfirmPopup = false;
		this.itemToDelete = null;
	}

	confirmDelete() {
		if (!this.itemToDelete) return;

		if (this.itemToDelete.type === 'assessment') {
			this.isDeletingAssignment = this.itemToDelete.id;
			this.candidateApi.deleteCandidateAssignment(this.candidateId, this.itemToDelete.id).pipe(
				tap((response) => {
					if (response.Success) {
						this.toastr.success('Assignment deleted successfully');
						this.loadCandidate();
					} else {
						this.toastr.error(response.Message || 'Failed to delete assignment');
					}
				}), finalize(() => {
					this.isDeletingAssignment = null;
					this.cancelDelete();
				})
			).subscribe();
		} else if (this.itemToDelete.type === 'interview') {
			this.isDeletingInterview = this.itemToDelete.id;
			this.candidateApi.deleteCandidateInterview(this.candidateId, this.itemToDelete.id).pipe(
				tap((response: any) => {
					if (response.success ?? response.Success) {
						this.toastr.success('AI Interview deleted successfully');
						this.loadCandidate();
					} else {
						this.toastr.error(response.message ?? response.Message ?? 'Failed to delete AI interview');
					}
				}), finalize(() => {
					this.isDeletingInterview = null;
					this.cancelDelete();
				})
			).subscribe();
		}
	}

	getAssessmentStatusText(assessment: any, job?: any): string {
		// 1. Primary Source of Truth: Backend explicitly provides 'status' or 'is_passed'
		if (assessment?.status) {
			const status = assessment.status.toLowerCase();
			if (status === 'pass' || status === 'passed') return 'Pass';
			if (status === 'fail' || status === 'failed') return 'Fail';
			if (status === 'in_progress' || status === 'in progress') return 'In Progress';
		}

		if (assessment?.is_passed === true) return 'Pass';
		if (assessment?.is_passed === false) return 'Fail';

		// 2. Failsafe Fallback: For older components or lists that might only pass completion_status
		if (assessment?.completion_status) {
			const compStatus = assessment.completion_status.toLowerCase();
			if (compStatus === 'completed' || compStatus === 'submitted' || compStatus === 'auto submitted') {
				const score = assessment.score ?? assessment.assessment_score ?? 0;
				const passScore = job?.default_passing_score ?? 40;
				return score >= passScore ? 'Pass' : 'Fail';
			}
			if (compStatus === 'in progress' || compStatus === 'in_progress') return 'In Progress';
		}
		return 'Pending';
	}

	getAssessmentStatusClass(assessment: any, job?: any): string {
		const status = this.getAssessmentStatusText(assessment, job).toLowerCase();
		if (status === 'pass' || status === 'completed') return 'status-completed';
		if (status === 'fail') return 'status-failed';
		if (status === 'in progress') return 'status-progress';
		return 'status-pending';
	}

	canViewAssessmentReport(assessment: any): boolean {
		const compStatus = assessment?.completion_status?.toLowerCase();
		const invStatus = assessment?.invitation_status?.toLowerCase();

		return ['submitted', 'auto submitted', 'completed', 'pass', 'passed', 'fail', 'failed'].includes(compStatus) || ['pass', 'passed', 'fail', 'failed'].includes(invStatus);
	}

	getInterviewStatusText(interview: any): string {
		if (interview?.status) {
			const status = interview.status.toLowerCase();
			if (status === 'invited') return 'Invited';
			if (status === 'in_progress' || status === 'in progress') return 'In Progress';
			if (status === 'pass' || status === 'passed') return 'Pass';
			if (status === 'fail' || status === 'failed') return 'Fail';
			if (status === 'completed' || status === 'graded') return 'Completed';
		}
		return 'Pending';
	}

	getInterviewStatusClass(interview: any): string {
		const status = this.getInterviewStatusText(interview).toLowerCase();
		if (status === 'completed' || status === 'pass' || status === 'passed') return 'status-completed';
		if (status === 'in progress') return 'status-progress';
		if (status === 'fail' || status === 'failed') return 'status-failed';
		return 'status-pending';
	}

	canViewInterviewReport(interview: any): boolean {
		const status = interview?.status?.toLowerCase() || '';
		const invStatus = interview?.invitation_status?.toLowerCase() || '';

		return ['completed', 'graded', 'pass', 'passed', 'fail', 'failed'].includes(status) || ['pass', 'passed', 'fail', 'failed', 'graded'].includes(invStatus);
	}

	onAssessmentActionChange(assessment: any, event: any): void {
		const action = event.target.value;
		if (action === 'Delete') {
			this.deleteCandidateAssignment(assessment.assignment_id);
			event.target.value = this.getAssessmentStatusText(assessment, this.jobCardVMs.find(j => j.assessment?.assignment_id === assessment.assignment_id)); // reset dropdown display
		}
	}

	onInterviewActionChange(interview: any, event: any): void {
		const action = event.target.value;
		if (action === 'Delete') {
			this.deleteCandidateInterview(interview.assignment_id);
			event.target.value = this.getInterviewStatusText(interview); // reset dropdown display
		}
	}

	openUpdateStatusModal(item: any, type: 'job' | 'assessment' | 'interview' = 'job'): void {
		this.selectedItemForUpdate = item;
		this.selectedItemType = type;
		if (type === 'job') {
			this.newUpdateStatus = item.final_status && item.final_status !== 'Not Updated' ? item.final_status : 'Applied';
		} else if (type === 'assessment') {
			this.newUpdateStatus = this.getAssessmentStatusText(item, this.jobCardVMs.find(j => j.assessment?.assignment_id === item.assignment_id));
		} else {
			this.newUpdateStatus = this.getInterviewStatusText(item);
		}
		this.newUpdateComment = '';
		this.showUpdateStatusModal = true;
	}

	closeUpdateStatusModal(): void {
		this.showUpdateStatusModal = false;
		this.selectedItemForUpdate = null;
		this.selectedItemType = null;
	}

	submitItemStatusUpdate(): void {
		if (!this.selectedItemForUpdate || !this.selectedItemType) return;

		if (this.selectedItemType === 'job') {
			const jobId = this.selectedItemForUpdate.job_id;
			const newStatusStr = this.newUpdateStatus;
			const commentStr = this.newUpdateComment.trim();
			const lowerStatus = newStatusStr.toLowerCase().replace(/\s+/g, '_');

			this.isUpdatingStatus = true;

			if (['selected', 'rejected', 'on_hold', 'on hold'].includes(newStatusStr.toLowerCase())) {
				const finalStat = lowerStatus === 'on_hold' ? 'on_hold' : lowerStatus;
				this.candidateApi.updateJobFinalStatus(this.candidateId, jobId, finalStat, commentStr).pipe(
					tap((res) => {
						if (res.Success) {
							this.toastr.success(res.Message || 'Job status updated successfully');
							this.closeUpdateStatusModal();
							this.loadCandidate();
						} else {
							this.toastr.error(res.Message || 'Failed to update status');
						}
					}),
					catchError((err) => {
						this.toastr.error(err?.error?.detail ?? 'Failed to update status');
						return of(null);
					}),
					finalize(() => {
						this.isUpdatingStatus = false;
					})
				).subscribe();
			} else {
				const backendStatus = this.reverseStatusMap[newStatusStr] || lowerStatus;
				this.candidateApi.changeStatus(this.candidateId, backendStatus, jobId).pipe(
					tap((res) => {
						if (res.Success) {
							this.toastr.success(res.Message || 'Status updated successfully');
							this.closeUpdateStatusModal();
							this.loadCandidate();
						} else {
							this.toastr.error(res.Message || 'Failed to update status');
						}
					}),
					catchError((err) => {
						this.toastr.error(err?.error?.detail ?? 'Failed to update status');
						return of(null);
					}),
					finalize(() => {
						this.isUpdatingStatus = false;
					})
				).subscribe();
			}
			return;
		}

		const assignmentId = this.selectedItemForUpdate.assignment_id || this.selectedItemForUpdate.id;
		if (!assignmentId) {
			this.closeUpdateStatusModal();
			return;
		}

		if (this.selectedItemType === 'assessment') {
			const assessment = this.assignedAssessments.find(a => a.assignment_id === assignmentId);
			if (assessment) {
				if (this.newUpdateStatus === 'Completed') {
					assessment.completion_status = 'completed';
				} else if (this.newUpdateStatus === 'In Progress') {
					assessment.completion_status = 'in progress';
				} else {
					assessment.completion_status = 'pending';
				}
			}
		} else {
			const interview = this.assignedAIInterviews.find(i => i.assignment_id === assignmentId);
			if (interview) {
				if (this.newUpdateStatus === 'Completed') {
					interview.status = 'completed';
				} else if (this.newUpdateStatus === 'In Progress') {
					interview.status = 'in progress';
				} else {
					interview.status = 'pending';
				}
			}
		}

		if (!this.itemLogs[assignmentId]) {
			this.itemLogs[assignmentId] = [];
		}

		const commentText = this.newUpdateComment.trim() || `Status updated to ${this.newUpdateStatus}`;

		this.itemLogs[assignmentId].unshift({
			status: this.newUpdateStatus,
			comment: commentText,
			timestamp: new Date()
		});

		this.closeUpdateStatusModal();
	}

	getItemLogs(assignmentId: string): any[] {
		return this.itemLogs[assignmentId] || [];
	}

	initializeMockItemLogs(): void {
		if (this.assignedAssessments) {
			this.assignedAssessments.forEach(a => {
				const id = a.assignment_id;
				if (!this.itemLogs[id]) {
					const job = this.jobCardVMs.find(j => j.assessment?.assignment_id === a.assignment_id);
					const statusText = this.getAssessmentStatusText(a, job);
					this.itemLogs[id] = [
						{
							status: 'Pending',
							comment: `Assessment assigned to candidate.`,
							timestamp: a.assigned_at ? new Date(a.assigned_at) : new Date(Date.now() - 24 * 3600 * 1000)
						}
					];
					if (statusText === 'In Progress') {
						this.itemLogs[id].unshift({
							status: 'In Progress',
							comment: `Candidate started the assessment.`,
							timestamp: new Date(Date.now() - 12 * 3600 * 1000)
						});
					} else if (statusText === 'Completed') {
						this.itemLogs[id].unshift({
							status: 'Completed',
							comment: `Candidate submitted assessment quiz. Score generated.`,
							timestamp: a.submitted_at ? new Date(a.submitted_at) : new Date(Date.now() - 6 * 3600 * 1000)
						});
					}
				}
			});
		}

		if (this.assignedAIInterviews) {
			this.assignedAIInterviews.forEach(i => {
				const id = i.assignment_id;
				if (!this.itemLogs[id]) {
					const statusText = this.getInterviewStatusText(i);
					this.itemLogs[id] = [
						{
							status: 'Pending',
							comment: `AI Interview invitation sent to candidate.`,
							timestamp: i.assigned_at ? new Date(i.assigned_at) : new Date(Date.now() - 48 * 3600 * 1000)
						}
					];
					if (statusText === 'In Progress') {
						this.itemLogs[id].unshift({
							status: 'In Progress',
							comment: `Candidate entered the interview room and started the video session.`,
							timestamp: new Date(Date.now() - 24 * 3600 * 1000)
						});
					} else if (statusText === 'Completed') {
						this.itemLogs[id].unshift({
							status: 'Completed',
							comment: `AI Interview completed. AI evaluation and grading generated.`,
							timestamp: i.submitted_at ? new Date(i.submitted_at) : new Date(Date.now() - 12 * 3600 * 1000)
						});
					}
				}
			});
		}
	}

	openLogsModal(item: any): void {
		this.selectedItemForLogs = item;
		this.showLogsModal = true;
	}

	closeLogsModal(): void {
		this.showLogsModal = false;
		this.selectedItemForLogs = null;
	}



	openAssessmentSubmitDialogBox(): void {
		if (!this.selectedAssessment) return;
		this.isSelectLinkModalOpen = false;
		const dialogRef = this.dialog.open(AssignmentSubmitDialogBoxComponent, {
			data: {
				assignmentLinkName: this.selectedAssessment.link_name,
				assignmentLinkUrl: this.selectedAssessment.link_url || this.selectedAssessment.url,
				assessmentTimeLimit: this.selectedAssessment.time_limit,
				expiryDate: this.selectedAssessment.end_date,
				userName: this.candidate?.name,
				userEmail: this.candidate?.email
			}
		});
		dialogRef.afterClosed().subscribe(result => {
			if (result) {
				this.assignAssessment();
			} else {
				this.isSelectLinkModalOpen = true;
			}
		});
	}


	get hasActiveLinks(): boolean {
		return this.availableLinks.some(link => !link.status || link.status?.toLowerCase() === 'active');
	}

	get hasActiveQuiz(): boolean {
		if (!this.availableAssessments || this.availableAssessments.length === 0) return false;
		return this.availableAssessments.some(quiz =>
			!quiz.status ||
			quiz.status.toLowerCase() === 'published' ||
			quiz.status.toLowerCase() === 'active'
		);
	}

	isRoundExpired(round: any): boolean {
		if (round.is_valid === false) return true;
		if (round.status?.toLowerCase().includes('expire')) return true;
		if (round.valid_until) {
			return new Date() > new Date(round.valid_until);
		}
		return false;
	}

	get hasActiveRounds(): boolean {
		return this.availableRounds.some(round => !this.isRoundExpired(round));
	}


	isLinkAlreadyAssigned(linkId: string): boolean {
		if (!linkId) return false;
		return this.assignedAssessments.some(a => a.link_id === linkId);
	}

	isRoundAlreadyAssigned(round: any): boolean {
		if (!round || !round.interview_url) return false;
		const roundUrlClean = round.interview_url.toLowerCase().trim();
		return this.assignedAIInterviews.some(i => {
			if (!i || !i.interview_url) return false;
			return i.interview_url.toLowerCase().trim() === roundUrlClean;
		});
	}

	ngOnDestroy(): void {
		if (this.toastTimer) clearTimeout(this.toastTimer);
	}


}